import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@material-tailwind/react";
import "./App.css";

// Desktop Layout Components
import Navbar from "./Components/layout/Navbar";
import Footer from "./Components/layout/Footer";

// Mobile App Layout Components
import MobileTopHeader from "./Components/layout/MobileTopHeader";
import MobileBottomNav from "./Components/layout/MobileBottomNav";
import ScrollToTop from "./Components/utils/ScrollToTop";
import { MessageCircle } from "lucide-react";

// Lazy load page components
const HomePage = lazy(() => import("./Pages/Home/HomePage"));
const AboutUs = lazy(() => import("./Pages/AboutUs/AboutUs"));
const ContactUs = lazy(() => import("./Pages/ContactUs/ContactUs"));
const ManufacturePage = lazy(() => import("./Pages/Manufacture/Manufacture"));
const ProductsPage = lazy(() => import("./Pages/Products/ProductsPage"));
const SlipFlanges = lazy(() => import("./Pages/Products/SlipFlanges"));
const WeldNeck = lazy(() => import("./Pages/Products/WeldNeck"));
const Blind = lazy(() => import("./Pages/Products/Blind"));
const SocketWeld = lazy(() => import("./Pages/Products/SocketWeld"));
const Threaded = lazy(() => import("./Pages/Products/Threaded"));
const Reducing = lazy(() => import("./Pages/Products/Reducing"));
const LapJoint = lazy(() => import("./Pages/Products/LapJoint"));
const Plate = lazy(() => import("./Pages/Products/Plate"));
const RfRij = lazy(() => import("./Pages/Products/RfRij"));
const LongWeld = lazy(() => import("./Pages/Products/LongWeld"));

const ManufactureCapacity = lazy(() =>
  import("./Pages/ManufacturingCapacity/ManufacturingCapacity")
);
const QualityControl = lazy(() => import("./Pages/QualityControl/QualityControl"));

// Clean Loading Spinner
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 space-y-3">
    <div className="w-10 h-10 border-3 border-slate-200 border-t-[#12385a] rounded-full animate-spin"></div>
    <span className="text-xs font-semibold text-slate-500 tracking-wide uppercase">
      Loading Auric...
    </span>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <div className="flex flex-col min-h-screen w-full max-w-full bg-white text-gray-900 font-sans">

          {/* ─── 1. Desktop Navigation Bar (Screens >= md) ─── */}
          <div className="hidden md:block fixed top-0 left-0 right-0 z-50  z-50 w-full">
            <Navbar />
          </div>

          {/* ─── 2. Mobile Native Top App Bar (Mobile Viewports < md) ─── */}
          <div className="block md:hidden fixed top-0 left-0 right-0 z-50  z-50 w-full">
            <MobileTopHeader />
          </div>

          {/* ─── 3. Main Content Area ─── */}
          <main className="flex-1 w-full max-w-full overflow-x-hidden">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/manufacture" element={<ManufacturePage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/slip-on" element={<SlipFlanges />} />
                <Route path="/products/weld-neck" element={<WeldNeck />} />
                <Route path="/products/blind" element={<Blind />} />
                <Route path="/products/socket-weld" element={<SocketWeld />} />
                <Route path="/products/socket" element={<SocketWeld />} />
                <Route path="/products/threaded" element={<Threaded />} />
                <Route path="/products/reducing" element={<Reducing />} />
                <Route path="/products/lap-joint" element={<LapJoint />} />
                <Route path="/products/plate" element={<Plate />} />
                <Route path="/products/companion" element={<Plate />} />
                <Route path="/products/rf-rij" element={<RfRij />} />
                <Route path="/products/studding" element={<RfRij />} />
                <Route path="/products/long-weld" element={<LongWeld />} />
                <Route
                  path="/manufacturing-capacity"
                  element={<ManufactureCapacity />}
                />
                <Route path="/quality-control" element={<QualityControl />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/aboutus" element={<AboutUs />} />
              </Routes>
            </Suspense>
          </main>

          {/* ─── 4. Mobile Floating WhatsApp Quick Action ─── */}
          <div className="block md:hidden">
            <a
              href="https://wa.me/917434905789?text=Hello%20Auric%20Engineering,%20I%20would%20like%20to%20request%20a%20quote%20for%20industrial%20flanges."
              target="_blank"
              rel="noopener noreferrer"
              className="fixed bottom-20 right-4 z-40 w-12 h-12 rounded-full bg-emerald-500 text-white shadow-xl flex items-center justify-center hover:bg-emerald-600 active:scale-95 transition-transform"
              aria-label="Chat on WhatsApp"
            >
              <MessageCircle className="w-6 h-6 fill-white" />
            </a>
          </div>

          {/* ─── 5. Footer (Displayed on both Desktop & Mobile) ─── */}
          <div className="pb-16 md:pb-0">
            <Footer />
          </div>

          {/* ─── 6. Mobile Bottom Tab Bar (Mobile Viewports < md) ─── */}
          <div className="block md:hidden">
            <MobileBottomNav />
          </div>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
